import { useState, useEffect } from 'react'
import { useUpdateSegment } from '../../hooks/useSegments'
import { Segment } from '../../types'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'

interface EditSegmentDialogProps {
  segment: Segment | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function EditSegmentDialog({ segment, open, onOpenChange }: EditSegmentDialogProps) {
  const updateMutation = useUpdateSegment()
  const [formData, setFormData] = useState({
    label: '',
    distance_km: '',
    elev_gain_m: '',
    elev_loss_m: '',
    planned_stop_min: ''
  })

  useEffect(() => {
    if (segment) {
      setFormData({
        label: segment.label || '',
        distance_km: segment.distance_km.toString(),
        elev_gain_m: (segment.elev_gain_m || 0).toString(),
        elev_loss_m: (segment.elev_loss_m || 0).toString(),
        planned_stop_min: (segment.planned_stop_min || 0).toString()
      })
    }
  }, [segment])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!segment) return

    try {
      await updateMutation.mutateAsync({
        id: segment.id,
        updates: {
          label: formData.label || null,
          distance_km: parseFloat(formData.distance_km),
          elev_gain_m: parseInt(formData.elev_gain_m) || 0,
          elev_loss_m: parseInt(formData.elev_loss_m) || 0,
          planned_stop_min: parseFloat(formData.planned_stop_min) || 0
        }
      })
      
      onOpenChange(false)
    } catch (error) {
      console.error('Failed to update segment:', error)
    }
  }

  if (!segment) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Segment</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="label">Label</Label>
            <Input
              id="label"
              value={formData.label}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, label: e.target.value }))}
              placeholder="e.g., Forest Loop, Climb to Peak"
            />
          </div>

          <div>
            <Label htmlFor="distance">Distance (km) *</Label>
            <Input
              id="distance"
              type="number"
              step="0.01"
              min="0.10"
              value={formData.distance_km}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, distance_km: e.target.value }))}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="elevGain">Elevation Gain (m)</Label>
              <Input
                id="elevGain"
                type="number"
                min="0"
                value={formData.elev_gain_m}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, elev_gain_m: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="elevLoss">Elevation Loss (m)</Label>
              <Input
                id="elevLoss"
                type="number"
                min="0"
                value={formData.elev_loss_m}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, elev_loss_m: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="plannedStop">Planned Stop (minutes)</Label>
            <Input
              id="plannedStop"
              type="number"
              step="0.5"
              min="0"
              value={formData.planned_stop_min}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, planned_stop_min: e.target.value }))}
            />
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Updating...' : 'Update Segment'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}